import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Folder, Briefcase, Edit2, Archive, RotateCcw, CheckCircle2,
  X, Calendar as CalIcon, ListChecks, Sparkles, Layers, AlertTriangle,
  PauseCircle, ArrowDownUp,
} from 'lucide-react';
import { projectsApi, Project, ProjectCreatePayload, ProjectUpdatePayload } from '../../api/projects';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DateField } from '../../components/ui/DateField';
import { clientsApi, Client } from '../../api/clients';

type StatusKey = 'ALL' | 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'ARCHIVED';

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'ARCHIVED', label: 'Archived' },
] as const;

const STATUS_META: Record<string, { color: string; bg: string; label: string }> = {
  ACTIVE:    { color: '#16a34a', bg: 'rgba(22,163,74,0.10)',  label: 'Active' },
  ON_HOLD:   { color: '#d97706', bg: 'rgba(217,119,6,0.10)',  label: 'On Hold' },
  COMPLETED: { color: '#2563eb', bg: 'rgba(37,99,235,0.10)',  label: 'Completed' },
  ARCHIVED:  { color: '#6b7280', bg: 'rgba(107,114,128,0.10)', label: 'Archived' },
};

interface FormState {
  name: string;
  description: string;
  code: string;
  client_id: string;
  status: 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'ARCHIVED';
  start_date: string;
  end_date: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  code: '',
  client_id: '',
  status: 'ACTIVE',
  start_date: '',
  end_date: '',
};

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusKey>('ALL');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { page_size: 200, include_archived: true };
      if (search) params.search = search;
      if (clientFilter) params.client_id = clientFilter;
      const r = await projectsApi.list(params);
      setProjects(r.data?.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [search, clientFilter]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    clientsApi.list({ page_size: 200 })
      .then(r => setClients(r.data?.items || []))
      .catch(() => {});
  }, []);

  const counts = useMemo(() => {
    const c: Record<string, number> = { ALL: 0, ACTIVE: 0, ON_HOLD: 0, COMPLETED: 0, ARCHIVED: 0 };
    projects.forEach(p => {
      c.ALL += 1;
      c[p.status] = (c[p.status] || 0) + 1;
    });
    return c;
  }, [projects]);

  const filtered = useMemo(() => {
    if (statusFilter === 'ALL') return projects;
    return projects.filter(p => p.status === statusFilter);
  }, [projects, statusFilter]);

  const totalTasks = useMemo(
    () => projects.reduce((acc, p) => acc + (p.task_counts?.total || 0), 0),
    [projects],
  );

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (p: Project) => {
    setEditing(p);
    setForm({
      name: p.name,
      description: p.description || '',
      code: p.code || '',
      client_id: p.client_id || '',
      status: p.status,
      start_date: p.start_date || '',
      end_date: p.end_date || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload: ProjectCreatePayload | ProjectUpdatePayload = {
        name: form.name.trim(),
        description: form.description || undefined,
        code: form.code || undefined,
        client_id: form.client_id || null,
        status: form.status,
        start_date: form.start_date || undefined,
        end_date: form.end_date || undefined,
      };
      if (editing) {
        await projectsApi.update(editing.id, payload);
      } else {
        await projectsApi.create(payload as ProjectCreatePayload);
      }
      closeModal();
      await loadProjects();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (p: Project) => {
    if (!window.confirm(`Archive project "${p.name}"?`)) return;
    try {
      await projectsApi.delete(p.id);
      await loadProjects();
    } catch {
      alert('Failed to archive');
    }
  };

  const handleReopen = async (p: Project) => {
    try {
      await projectsApi.reopen(p.id);
      await loadProjects();
    } catch {
      alert('Failed to reopen');
    }
  };

  const handleComplete = async (p: Project) => {
    if (!window.confirm(`Mark project "${p.name}" as completed?`)) return;
    try {
      await projectsApi.complete(p.id);
      await loadProjects();
    } catch {
      alert('Failed to complete');
    }
  };

  const statusTiles: Array<{ key: StatusKey; label: string; tone: string; icon: any }> = [
    { key: 'ALL',       label: 'All',       tone: 'all',       icon: Layers },
    { key: 'ACTIVE',    label: 'Active',    tone: 'active',    icon: Sparkles },
    { key: 'ON_HOLD',   label: 'On Hold',   tone: 'hold',      icon: PauseCircle },
    { key: 'COMPLETED', label: 'Completed', tone: 'completed', icon: CheckCircle2 },
    { key: 'ARCHIVED',  label: 'Archived',  tone: 'archived',  icon: Archive },
  ];

  const tileStyle = (tone: string, active: boolean): React.CSSProperties => {
    const palette: Record<string, { bg: string; fg: string; border: string }> = {
      all:       { bg: 'rgba(34,197,94,0.10)',  fg: '#15803d', border: 'rgba(34,197,94,0.25)' },
      active:    { bg: 'rgba(34,197,94,0.12)',  fg: '#166534', border: 'rgba(34,197,94,0.30)' },
      hold:      { bg: 'rgba(234,179,8,0.12)',  fg: '#854d0e', border: 'rgba(234,179,8,0.30)' },
      completed: { bg: 'rgba(59,130,246,0.12)', fg: '#1e40af', border: 'rgba(59,130,246,0.30)' },
      archived:  { bg: 'rgba(107,114,128,0.10)',fg: '#374151', border: 'rgba(107,114,128,0.25)' },
    };
    const p = palette[tone];
    return {
      background: active ? p.fg : p.bg,
      color: active ? '#fff' : p.fg,
      border: `1px solid ${active ? p.fg : p.border}`,
      borderRadius: 12,
      padding: '14px 16px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      boxShadow: active ? '0 4px 12px -4px rgba(0,0,0,0.12)' : 'none',
    };
  };

  if (loading) {
    return (
      <div className="gt-shell flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="gt-loading-ring mx-auto mb-3" />
          <p className="gt-page-sub gt-serif-italic">Loading <em>projects</em>…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gt-shell flex items-center justify-center min-h-[60vh]">
        <div className="gt-card p-8 max-w-md text-center" style={{ borderColor: 'rgba(220,38,38,0.18)' }}>
          <AlertTriangle className="w-9 h-9 mx-auto mb-3" style={{ color: 'var(--gt-dnd)' }} />
          <p className="gt-display" style={{ fontSize: 14, color: 'var(--gt-text-primary)' }}>{error}</p>
          <button onClick={loadProjects} className="gt-btn is-danger mt-4 mx-auto">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
        {/* Header */}
        <div className="gt-page-hero flex-col md:flex-row gap-4">
          <div>
            <div className="gt-eyebrow-row">
              <span className="gt-leaf" />
              <span className="gt-eyebrow">i. Workspace</span>
            </div>
            <h1 className="gt-page-title">Project <em>Management</em></h1>
            <p className="gt-page-sub" style={{ marginTop: 6 }}>
              Group and track your <em className="gt-serif-italic">organizational</em> projects.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={openCreate} className="gt-btn is-primary is-lg">
              <Plus className="w-4 h-4" />
              New Project
            </button>
          </div>
        </div>

        {/* Stat Cards */}
        {projects.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="gt-stat">
              <div className="gt-stat-label"><Folder className="w-3.5 h-3.5" /> Total Projects</div>
              <p className="gt-stat-value">{counts.ALL}</p>
            </div>
            <div className="gt-stat">
              <div className="gt-stat-label"><Sparkles className="w-3.5 h-3.5" /> Active</div>
              <p className="gt-stat-value" style={{ color: 'var(--gt-accent-strong)' }}>{counts.ACTIVE || 0}</p>
            </div>
            <div className="gt-stat">
              <div className="gt-stat-label"><ListChecks className="w-3.5 h-3.5" /> Linked Tasks</div>
              <p className="gt-stat-value">{totalTasks}</p>
            </div>
          </div>
        )}

        {/* Status Tile Filter */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {statusTiles.map(t => {
            const Icon = t.icon;
            const isActive = statusFilter === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setStatusFilter(t.key)}
                style={tileStyle(t.tone, isActive)}
              >
                <span className="flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5" />
                  <span className="text-xs uppercase tracking-wide font-semibold">{t.label}</span>
                </span>
                <span className="text-lg font-bold gt-numerals">{counts[t.key] || 0}</span>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="gt-card overflow-hidden">
          <button
            onClick={() => setShowFilters(s => !s)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <span className="flex items-center gap-2 gt-display" style={{ fontSize: 14, color: 'var(--gt-text-primary)' }}>
              <Search className="w-4 h-4" /> Filters
            </span>
            <span className="gt-page-sub text-xs">{showFilters ? 'Hide' : 'Show'}</span>
          </button>
          {showFilters && (
            <div className="px-4 pb-4 flex flex-col md:flex-row gap-3 md:items-center border-t border-gray-100 pt-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search projects..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
              </div>
              <div className="min-w-[180px]">
                <Select
                  value={clientFilter || "__all__"}
                  onValueChange={(v) => setClientFilter(v === "__all__" ? "" : v)}
                >
                  <SelectTrigger><SelectValue placeholder="All Clients" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Clients</SelectItem>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.clientName}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        {/* Body */}
        {filtered.length === 0 ? (
          <div className="gt-card p-12 text-center">
            <div className="gt-empty">
              <div className="gt-empty-leaf"><Folder className="w-6 h-6" /></div>
              <div className="gt-empty-title">
                {projects.length === 0 ? 'No projects yet' : 'No projects in this view'}
              </div>
              <div className="gt-page-sub">
                {projects.length === 0
                  ? <>A clean slate. <em className="gt-serif-italic">Begin</em> with your first project.</>
                  : 'Try a different status filter.'}
              </div>
              {projects.length === 0 && (
                <button onClick={openCreate} className="gt-btn is-primary mt-5 mx-auto">
                  <Plus className="w-4 h-4" /> Create your first project
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center">
              <p className="gt-page-sub gt-numerals">
                Showing <span className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>{filtered.length}</span> of{' '}
                <span className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>{counts.ALL}</span> projects
              </p>
              <div className="flex items-center gap-2 gt-page-sub" style={{ background: 'var(--gt-surface-2)', padding: '6px 12px', borderRadius: 999 }}>
                <ArrowDownUp className="w-3.5 h-3.5" />
                <span className="gt-eyebrow" style={{ marginBottom: 0 }}>Newest first</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map(p => <ProjectCard
                key={p.id}
                project={p}
                onView={() => navigate(`/projects/${p.id}`)}
                onEdit={() => openEdit(p)}
                onArchive={() => handleArchive(p)}
                onReopen={() => handleReopen(p)}
                onComplete={() => handleComplete(p)}
              />)}
            </div>
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-3 overflow-y-auto"
          style={{ background: 'rgba(15, 29, 20, 0.45)', backdropFilter: 'blur(6px)' }}
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-8"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <div className="gt-eyebrow-row" style={{ marginBottom: 4 }}>
                  <span className="gt-leaf" />
                  <span className="gt-eyebrow">{editing ? 'Edit' : 'New'}</span>
                </div>
                <h3 className="gt-display" style={{ fontSize: 18, color: 'var(--gt-text-primary)' }}>
                  {editing ? 'Edit Project' : 'Create Project'}
                </h3>
              </div>
              <button onClick={closeModal} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Project Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="e.g. Website Redesign"
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Code</label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="WEB-2026"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Status</label>
                  <Select
                    value={form.status}
                    onValueChange={(v) => setForm(f => ({ ...f, status: v as FormState['status'] }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Client <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <Select
                  value={form.client_id || "__none__"}
                  onValueChange={(v) => setForm(f => ({ ...f, client_id: v === "__none__" ? "" : v }))}
                >
                  <SelectTrigger><SelectValue placeholder="Select Client" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— No client —</SelectItem>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.clientName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Brief description..."
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Start Date</label>
                  <DateField
                    value={form.start_date}
                    onChange={(v) => setForm(f => ({ ...f, start_date: v }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">End Date</label>
                  <DateField
                    value={form.end_date}
                    onChange={(v) => setForm(f => ({ ...f, end_date: v }))}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button
                onClick={closeModal}
                disabled={saving}
                className="gt-btn is-ghost"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="gt-btn is-primary"
              >
                {saving ? 'Saving...' : editing ? 'Update Project' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface ProjectCardProps {
  project: Project;
  onView: () => void;
  onEdit: () => void;
  onArchive: () => void;
  onReopen: () => void;
  onComplete: () => void;
}

function ProjectCard({ project: p, onView, onEdit, onArchive, onReopen, onComplete }: ProjectCardProps) {
  const counts = p.task_counts || {};
  const meta = STATUS_META[p.status] || STATUS_META.ACTIVE;
  const total = counts.total || 0;
  const open = counts.open || 0;
  const completed = total - open;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isClosed = p.status === 'ARCHIVED' || p.status === 'COMPLETED';

  return (
    <div
      className="gt-card p-5 flex flex-col cursor-pointer transition-all hover:-translate-y-0.5"
      style={{
        opacity: p.status === 'ARCHIVED' ? 0.75 : 1,
        borderLeft: `3px solid ${meta.color}`,
      }}
      onClick={onView}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <h3
            className="gt-display truncate"
            style={{ fontSize: 15, color: 'var(--gt-text-primary)', fontWeight: 600 }}
          >
            {p.name}
          </h3>
          {p.code && (
            <p className="gt-page-sub gt-numerals" style={{ fontSize: 11, marginTop: 2 }}>
              {p.code}
            </p>
          )}
        </div>
        <span
          className="text-[10px] uppercase tracking-wide px-2 py-1 rounded-full font-bold whitespace-nowrap"
          style={{ background: meta.bg, color: meta.color }}
        >
          {meta.label}
        </span>
      </div>

      {/* Description */}
      {p.description && (
        <p className="gt-page-sub line-clamp-2 mb-3" style={{ fontSize: 13 }}>
          {p.description}
        </p>
      )}

      {/* Meta */}
      <div className="space-y-1.5 mb-3">
        {p.client_name && (
          <div
            className="inline-flex items-center gap-1.5"
            style={{
              padding: '3px 9px',
              background: 'var(--gt-primary-container)',
              color: 'var(--gt-primary-deep)',
              borderRadius: 6,
              border: '1px solid color-mix(in srgb, var(--gt-primary) 25%, transparent)',
              fontFamily: 'var(--gt-font-display)',
              fontWeight: 600,
              fontSize: 10.5,
              maxWidth: '100%',
            }}
          >
            <Briefcase className="w-3 h-3" />
            <span className="truncate">{p.client_name}</span>
          </div>
        )}
        {(p.start_date || p.end_date) && (
          <div className="flex items-center gap-1.5 gt-page-sub gt-numerals" style={{ fontSize: 11 }}>
            <CalIcon className="w-3 h-3" />
            <span>{p.start_date || '...'} → {p.end_date || '...'}</span>
          </div>
        )}
      </div>

      {/* Progress */}
      <div className="mt-auto">
        <div className="flex items-center justify-between mb-1.5 text-xs">
          <span className="gt-page-sub flex items-center gap-1">
            <ListChecks className="w-3 h-3" />
            <span className="gt-numerals">{total} tasks</span>
            {open > 0 && <span className="gt-numerals">· {open} open</span>}
          </span>
          <span className="gt-numerals gt-display" style={{ color: 'var(--gt-text-primary)', fontWeight: 600 }}>
            {pct}%
          </span>
        </div>
        <div
          className="h-1.5 rounded-full overflow-hidden"
          style={{ background: 'var(--gt-surface-2)' }}
        >
          <div
            className="h-full transition-all"
            style={{ width: `${pct}%`, background: meta.color }}
          />
        </div>

        <div className="flex items-center gap-2 pt-3 mt-3 border-t border-gray-100">
          <button
            onClick={(e) => { e.stopPropagation(); onView(); }}
            className="flex-1 text-xs font-semibold text-blue-600 hover:text-blue-700 text-left"
          >
            View Tasks →
          </button>
          {isClosed ? (
            <button
              onClick={(e) => { e.stopPropagation(); onReopen(); }}
              className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
              title="Reopen"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          ) : (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onComplete(); }}
                className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                title="Mark as Completed"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(); }}
                className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title="Edit"
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onArchive(); }}
                className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Archive"
              >
                <Archive className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
